/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import { Button, Form, FormGroup, Label, Input, Col, InputGroup, InputGroupAddon } from 'reactstrap';
import Alert from 'react-s-alert';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';

// service imports
import AdminService from '../../services/AdminService';
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from '../general/ErrorBoundary';
import moment from 'moment';

const Payments = props => {

    // contexts
    const { theme } = useContext(ThemeContext);

    // states
    const [pendingPaymentRecords, setPendingPaymentRecords] = useState([]);
    const [pendingClientSubscriptionPaymentRecords, setPendingClientSubscriptionPaymentRecords] = useState([]);
    const [completedPaymentsRecords, setCompletedPaymentRecords] = useState([]);
    const [completedClientSubscriptionPaymentRecords, setCompletedClientSubscriptionPaymentRecords] = useState([]);
    const [totalPendingPayments, setTotalPendingPayments] = useState(0);
    const [totalPaidToProviders, setTotalPaidtoProviders] = useState(0);
    const [totalGSTCollected, setTotalGSTCollected] = useState(0);
    const [totalRazorpayCharges, setTotalRazorpayCharges] = useState(0);
    const [netIncome, setNetIncome] = useState(0);
    const [isLoading, setIsLoading] = useState([]);


    // useEffects
    useEffect(() => {
        AdminService.getPendingPayments()
            .then(data => {
                try {
                    setPendingPaymentRecords(data.pendingPayments);
                    setPendingClientSubscriptionPaymentRecords(data.pendingClientSubscriptionPayments);
                    setTotalPendingPayments(data.totalPendingPayments);
                    setTotalPaidtoProviders(data.totalPaidToProviders);
                    setTotalGSTCollected(data.totalGSTCollected);
                    // api
                    setTotalRazorpayCharges(data.totalRazorpayCharges);
                    setNetIncome(data.netIncome);
                } catch (err) {
                    Alert.error("Some error occured, please try again later or report support team.", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    GeneralService.logError('at Admin Payments useEffect_1', 'Error in try catch block', err.stack);
                }
            })
        AdminService.getCompletedPayments()
            .then(data => {
                try {
                    setCompletedPaymentRecords(data.completedPayments);
                } catch (err) {
                    Alert.error("Some error occured, please try again later or report support team.", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    GeneralService.logError('at Admin Payments useEffect_2', 'Error in try catch block', err.stack);
                }
            });
    }, []);

    // utility functions
    const markProcessed = (index) => {
        try {
            setIsLoading({ ...isLoading, [index]: true });
            AdminService.markPaymentProcessed(pendingPaymentRecords[index].providerEmail, pendingPaymentRecords[index].fromDate)
                .then(data => {
                    AdminService.getPendingPayments().then(data => {
                        setPendingPaymentRecords(data.pendingPayments);
                    });
                    AdminService.getCompletedPayments().then(data => {
                        setCompletedPaymentRecords(data.completedPayments);
                    })
                    setIsLoading({ ...isLoading, [index]: false });
                })
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading({ ...isLoading, [index]: false });
            GeneralService.logError('at Admin Payments markProcessed', 'Error in try catch block', err.stack);
        }
    }

    // handle functions
    const handleSearch = () => {
        // todo
    }

    // function's return
    return (
        <div className="container-fluid mb-5">
            <div>
                <h3 className="my-4">Counsellor Payments</h3>
            </div>

            <div className="">
                <div className="row">
                    <div className="text-center col-md-4 mt-3">
                        <div className="p-5" style={theme.summaryCard}>
                            <span style={theme.summaryCardValue}>₹ {totalPendingPayments}</span>
                            <br />
                            <span style={theme.summaryCardTitle}>Pending Payments</span>
                        </div>
                    </div>
                    <div className="text-center col-md-4 mt-3">
                        <div className="p-5" style={theme.summaryCard}>
                            <span style={theme.summaryCardValue}>₹ {totalPaidToProviders}</span>
                            <br />
                            <span style={theme.summaryCardTitle}>Total Paid To Counsellors</span>
                        </div>
                    </div>
                    <div className="text-center col-md-4 mt-3">
                        <div className="p-5" style={theme.summaryCard}>
                            <span style={theme.summaryCardValue}>₹ {totalGSTCollected}</span>
                            <br />
                            <span style={theme.summaryCardTitle}>Total GST Collected</span>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="text-center col-md-6 mt-3">
                        <div className="p-5" style={theme.summaryCard}>
                            <span style={theme.summaryCardValue}>₹ {totalRazorpayCharges}</span>
                            <br />
                            <span style={theme.summaryCardTitle}>Total Razorpay Charges</span>
                        </div>
                    </div>
                    <div className="text-center col-md-6 mt-3">
                        <div className="p-5" style={theme.summaryCard}>
                            <span style={theme.summaryCardValue}>₹ {netIncome}</span>
                            <br />
                            <span style={theme.summaryCardTitle}>Gross Income On Platform</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pending Payments Table */}
            <div className="card mb-4 mt-4">
                <div className="card-header">
                    Pending Payments
                </div>
                <div className="card-body py-0">
                    <div className="table-responsive">
                        <table className="table table-bordered mb-3" id="dataTable" width="100%" cellSpacing="0">
                            <thead>
                                <tr>
                                    <td colSpan="8" className="border-0 pr-0">
                                        <div className="d-flex">
                                            <div className="ml-auto">
                                                Search<input type="text" id="searchInput1" className="ml-2" style={{ marginRight: "-20px", paddingRight: "20px" }} onChange={handleSearch}></input>
                                                <span style={{ cursor: "pointer", marginRight: "7px", alignSelf: "center", zIndex: 100 }} onClick={() => { document.getElementById("searchInput1").value = ""; }}><i className="fa fa-times"></i></span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Dates</th>
                                    <th>Counsellor Name</th>
                                    <th>Counsellor Email</th>
                                    <th>Amount Earned</th>
                                    <th>Razorpay Charges</th>
                                    <th>Net amount after deducting razorpay charges</th>
                                    <th>Expected on</th>
                                    <th>Mark as Processed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    pendingPaymentRecords.length == 0
                                        ? <p>No records found</p>
                                        :

                                        pendingPaymentRecords.map((item, index) => (
                                            <ErrorBoundary errorAt={`At Admin Payments PendingTable${index}`}>
                                                <tr key={index}>
                                                    <td>{item.fromDate} to {item.toDate}</td>
                                                    <td>{item.providerName}</td>
                                                    <td>{item.providerEmail}</td>
                                                    <td>₹ {item.amountEarned}</td>
                                                    <td>₹ {item.razorpayCharges}</td>
                                                    <td>₹ {item.netAmountAfterDeductingRazorpayCharges}</td>
                                                    <td>{!item.isProcessed ? `Expected on ${item.expectedPaymentDate}` : `${item.expectedPaymentDate}`}</td>
                                                    <td>
                                                        <Button onClick={() => markProcessed(index)} style={theme.button} disabled={isLoading[index]}>{isLoading[index] ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Mark as Processed</Button>
                                                    </td>
                                                </tr>
                                            </ErrorBoundary>
                                        ))
                                }
                            </tbody>

                        </table>
                    </div>
                </div>
            </div>

            {/* Completed Payments Table */}
            <div className="card mb-4">
                <div className="card-header">
                    Completed Payments
                </div>
                <div className="card-body py-0">
                    <div className="table-responsive">
                        <table className="table table-bordered mb-3" id="dataTable" width="100%" cellSpacing="0">
                            <thead>
                                <tr>
                                    <td colSpan="8" className="border-0 pr-0">
                                        <div className="d-flex">
                                            <div className="ml-auto">
                                                Search<input type="text" id="searchInput2" className="ml-2" style={{ marginRight: "-20px", paddingRight: "20px" }} onChange={handleSearch}></input>
                                                <span style={{ cursor: "pointer", marginRight: "7px", alignSelf: "center", zIndex: 100 }} onClick={() => { document.getElementById("searchInput2").value = ""; }}><i className="fa fa-times"></i></span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Dates</th>
                                    <th>Counsellor Name</th>
                                    <th>Counsellor Email</th>
                                    <th>Amount Earned</th>
                                    <th>Razorpay Charges</th>
                                    <th>Net amount after deducting razorpay charges</th>
                                    <th>Was Expected on</th>
                                    <th>Processed on</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    completedPaymentsRecords.length == 0
                                        ? <p>No records found</p>
                                        :
                                        completedPaymentsRecords.map((item, index) => (
                                            <ErrorBoundary errorAt={`At Admin Payments CompletedTable${index}`}>
                                                <tr key={index}>
                                                    <td>{item.fromDate} to {item.toDate}</td>
                                                    <td>{item.providerName}</td>
                                                    <td>{item.providerEmail}</td>
                                                    <td>₹ {item.amountEarned}</td>
                                                    <td>₹ {item.razorpayCharges}</td>
                                                    <td>₹ {item.netAmountAfterDeductingRazorpayCharges}</td>
                                                    <td>{item.expectedPaymentDate}</td>
                                                    <td>{item.paidOnDate}</td>
                                                    { }
                                                </tr>
                                            </ErrorBoundary>
                                        ))
                                }
                            </tbody>

                        </table>
                    </div>
                </div>
            </div>

            {/* Pending Client Subscription Payments Table */}
            <div className="card mb-4 mt-4">
                <div className="card-header">
                    Pending Client Subscription Payments
                </div>
                <div className="card-body py-0">
                    <div className="table-responsive">
                        <table className="table table-bordered mb-3" id="dataTable" width="100%" cellSpacing="0">
                            <thead>
                                <tr>
                                    <td colSpan="8" className="border-0 pr-0">
                                        <div className="d-flex">
                                            <div className="ml-auto">
                                                Search<input type="text" id="searchInput1" className="ml-2" style={{ marginRight: "-20px", paddingRight: "20px" }} /*onChange={handleSearch}*/></input>
                                                <span style={{ cursor: "pointer", marginRight: "7px", alignSelf: "center", zIndex: 100 }} /*onClick={() => { document.getElementById("searchInput1").value = ""; }}*/><i className="fa fa-times"></i></span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Dates</th>
                                    <th>Counsellor Name</th>
                                    <th>Counsellor Email</th>
                                    <th>Amount Earned</th>
                                    <th>Razorpay Charges</th>
                                    <th>Net amount after deducting charges</th>
                                    <th>Expected on</th>
                                    <th>Mark as Processed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    pendingClientSubscriptionPaymentRecords.length == 0
                                        ? <p>No records found</p>
                                        :
                                        pendingClientSubscriptionPaymentRecords.map((item, index) => (
                                            <ErrorBoundary errorAt={`At Admin Payments PendingTable${index}`}>
                                                <tr key={index}>
                                                    <td>{moment(item.fromDate).format("DD MMMM YYYY")} to {moment(item.toDate).format("DD MMMM YYYY")}</td>
                                                    <td>{item.providerName}</td>
                                                    <td>{item.providerEmail}</td>
                                                    <td>₹ {item.amountEarned}</td>
                                                    <td>₹ {item.razorpayCharges}</td>
                                                    <td>₹ {item.netAmountAfterDeductingCharges}</td>
                                                    <td>{!item.isProcessed ? `Expected on ${item.expectedPaymentDate}` : `${item.expectedPaymentDate}`}</td>
                                                    <td>
                                                        <Button /*onClick={() => markProcessed(index)}*/ style={theme.button} /*disabled={isLoading[index]}*/>{isLoading[index] ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Mark as Processed</Button>
                                                    </td>
                                                </tr>
                                            </ErrorBoundary>
                                        ))
                                }
                            </tbody>

                        </table>
                    </div>
                </div>
            </div>

            {/* Completed Client Subscription Payments Table */}
            <div className="card mb-4">
                <div className="card-header">
                    Completed Client Subscription
                </div>
                <div className="card-body py-0">
                    <div className="table-responsive">
                        <table className="table table-bordered mb-3" id="dataTable" width="100%" cellSpacing="0">
                            <thead>
                                <tr>
                                    <td colSpan="8" className="border-0 pr-0">
                                        <div className="d-flex">
                                            <div className="ml-auto">
                                                {/* Search<input type="text" id="searchInput2" className="ml-2" style={{ marginRight: "-20px", paddingRight: "20px" }} onChange={handleSearch}></input> */}
                                                {/* <span style={{ cursor: "pointer", marginRight: "7px", alignSelf: "center", zIndex: 100 }} onClick={() => { document.getElementById("searchInput2").value = ""; }}><i className="fa fa-times"></i></span> */}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Dates</th>
                                    <th>Counsellor Name</th>
                                    <th>Counsellor Email</th>
                                    <th>Amount Earned</th>
                                    <th>Razorpay Charges</th>
                                    <th>Net amount after deducting razorpay charges</th>
                                    <th>Was Expected on</th>
                                    <th>Processed on</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    completedClientSubscriptionPaymentRecords.length == 0
                                        ? <p>No records found</p>
                                        :
                                        completedClientSubscriptionPaymentRecords.map((item, index) => (
                                            <ErrorBoundary errorAt={`At Admin Payments CompletedTable${index}`}>
                                                <tr key={index}>
                                                    <td>{item.fromDate} to {item.toDate}</td>
                                                    <td>{item.providerName}</td>
                                                    <td>{item.providerEmail}</td>
                                                    <td>₹ {item.amountEarned}</td>
                                                    <td>₹ {item.razorpayCharges}</td>
                                                    <td>₹ {item.netAmountAfterDeductingRazorpayCharges}</td>
                                                    <td>{item.expectedPaymentDate}</td>
                                                    <td>{item.paidOnDate}</td>
                                                    { }
                                                </tr>
                                            </ErrorBoundary>
                                        ))
                                }
                            </tbody>

                        </table>
                    </div>
                </div>
            </div>

        </div>
    )

}

export default Payments;